'use client';
import React, { FC, ReactNode, useState } from 'react';
import cls from 'classnames';
import { ShareIcon } from '@bfe/moly-icons';
import { checkAccountToRegister, isApp } from '@spot-fe/utils';
import { useI18n, useUserInfo } from '@spot-fe/base-ui';
import { PrizeType, ProjectDetailV2, TaskType, TokenDetailData } from '@/apis';
import { getTokenNames, navToLogin } from '@/app/helper';
import { Button, Modal, TextLink, getProcessedUrl } from '@bfe/moly';
import { plus, safeToFormat1 as safeToFormat, gt } from '@/utils/number';
import styles from './index.module.scss';
import detailStyles from './detail.module.scss';
import { Media } from './media';
import { h5Jump, pcJump, uniTryJump } from '@spot-fe/app-bridge';
import { getReferralCenterUrl } from '@/utils/links';
import { useShare } from '@/components/Referral';
import { getPrizeInfo } from '@/hooks/useShare';
import AutoEllipsisText from '../AutoEllipsisText';

interface Item {
  keyword: ReactNode | string;
  amount: ReactNode;
}

export interface Props {
  isPc: boolean;
  title?: ReactNode;
  subTitle?: ReactNode;
  items?: Item[];
  // showTotal: boolean;
}

export const HomeHeader: FC<Props> = ({
  title,
  subTitle,
  isPc,
  // showTotal = true,
  items = [],
}) => {
  const { t } = useI18n();
  const { isLogin } = useUserInfo();

  const handleClick = (url) => {
    if (url) {
      if (isApp()) {
        h5Jump(`bybitapp://open/web?url=${encodeURIComponent(url)}`);
        return;
      }
      pcJump(url);
    }
  };

  const navToReferralCenter = () => {
    if (!isLogin) {
      if (checkAccountToRegister('spotx_tokensplash_ongoing')) {
        return;
      }
      navToLogin();
      return;
    }
    const link = getReferralCenterUrl({ source: 2 });
    handleClick(link);
  };

  return (
    <div className={styles.homeHeaderWrapper}>
      {isPc ? (
        <div className={cls(styles.homeHeader)}>
          <div className={styles.content}>
            <div className={styles.title} data-paint>
              {title}
            </div>
            <div className={styles.subTitle} data-paint>
              {subTitle}
            </div>
            {items.length ? (
              <div className={styles.info}>
                {items.map((item, index) => {
                  return (
                    <>
                      <div className={styles.item} key={index}>
                        <div className={styles.keyword}>{item.keyword}</div>
                        <div className={styles.amount}>{item.amount}</div>
                      </div>
                      {index + 1 < items?.length && (
                        <div className={styles.liner} />
                      )}
                    </>
                  );
                })}
              </div>
            ) : (
              <></>
            )}
            <div className={styles.referralArea}>
              <TextLink onClick={navToReferralCenter}>
                {t('referralHistRules')}
              </TextLink>
            </div>
          </div>
          <div className={styles.logo} />
        </div>
      ) : (
        <div className={cls(styles.homeHeaderH5)}>
          <div className={styles.contentH5TopBox}>
            <div className={styles.contentH5TopBoxLeft}>
              <div className={styles.titleH5} data-paint>
                {title}
              </div>
              <div className={styles.subTitleH5} data-paint>
                {subTitle}
              </div>
            </div>
            <div className={styles.logoH5} />
          </div>
          <div className={styles.infoH5}>
            {(items || []).map((item, index) => {
              return (
                <>
                  <div
                    className={cls(styles.item, {
                      [styles.right]: (index + 1) % 2 === 0,
                    })}
                    key={index}
                  >
                    <div className={styles.keyword}>{item.keyword}</div>
                    <div className={styles.amount}>{item.amount}</div>
                  </div>
                  {index + 1 < items?.length && (
                    <div className={styles.liner} />
                  )}
                </>
              );
            })}
          </div>
          <div className={styles.referralArea}>
            <TextLink onClick={navToReferralCenter}>
              {t('referralHistRules')}
            </TextLink>
          </div>
        </div>
      )}
    </div>
  );
};

export interface Duration {
  keyword: ReactNode;
  time: ReactNode;
  active: boolean;
}

export interface DurationsProps {
  durations?: Duration[];
  current?: number;
}

export const Durations: FC<DurationsProps> = ({ durations }) => {
  // const current = durations?.findIndex((item) => item.active)
  let current = 0;
  for (let i = 0; i < (durations || []).length; i++) {
    if ((durations || [])[i].active) {
      current = i;
    }
  }

  return (
    <div className={styles['durations-box']}>
      <div className={`${styles.durations}`}>
        {durations?.map((duration, index) => {
          const total = durations.length;
          const order = index + 1;
          const isActive = current == index;
          const todo =
            current != null ? current < index && order < total : false;
          const init = current != null ? current > index + 1 : false;
          return (
            <div
              key={index}
              className={cls(styles.item, {
                [styles.active]: isActive,
                [styles.future]: todo || init,
                [styles.last]: total === order,
              })}
            >
              <div
                className={styles.keyword}
                dangerouslySetInnerHTML={{ __html: duration.keyword as string }}
              />
              <div className={styles.time}>{duration.time}</div>
              <div className={styles.line} />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export interface DetailHeaderProps {
  isPc?: boolean;
  data?: ProjectDetailV2;
  tokenDetail?: TokenDetailData;
  toper?: ReactNode;
}

export const DetailHeader: FC<DetailHeaderProps> = ({
  isPc,
  toper,
  data,
  tokenDetail,
}) => {
  const { t } = useI18n();

  const { openShareModal } = useShare();
  const { isLogin } = useUserInfo();

  const [remarkVisible, setRemarkVisible] = useState(false);
  // 分享按钮
  const handleShare = () => {
    if (!isLogin) {
      navToLogin();
      return;
    }

    const info = getPrizeInfo(data);

    console.log('share info:', data, info);

    openShareModal({
      code: data?.code || '',
      prizepool: info?.prizePool || 0,
      token: info?.prizeToken || '',
    });
  };

  // 项目类型
  const typeMap = {
    [TaskType.Deposit]: 'detailHeaderTypeDeposit',
    [TaskType.Trade]: 'detailHeaderTypeTrade',
    [TaskType.All]: 'detailHeaderTypeAll',
  };
  // 标题币种, 默认是交易币种
  let token = data?.tradeToken;
  let depositToken = '';
  let depositPrizeToken = '';
  let prizePool = '';
  let prizeToken = data?.newUserPrizeToken;
  // 处理新老用户逻辑
  switch (data?.prizeType) {
    case PrizeType.New:
      // 新用户项目
      depositToken = getTokenNames(data?.newUserCondition1);
      depositPrizeToken = data?.newUserPrizeToken;
      break;
    case PrizeType.Old:
      // 老用户项目
      depositToken = getTokenNames(data?.oldUserCondition1);
      depositPrizeToken = data?.oldUserPrizeToken;
      break;
    default:
      // 通用项目
      depositToken = getTokenNames([
        ...(data?.oldUserCondition1 || []),
        ...(data?.newUserCondition1 || []),
      ]);
      depositPrizeToken = data?.newUserPrizeToken || '';
  }
  // 处理项目类型逻辑
  switch (data?.taskType) {
    case TaskType.Deposit:
      // 入金项目
      token = depositToken;
      prizeToken = depositPrizeToken;
      prizePool = plus(data?.newUserPrizeTotal, data?.oldUserPrizeTotal);
      break;
    case TaskType.Trade:
      // 交易项目
      prizePool = data?.tradeUserPrizeTotal;
      prizeToken = data?.tradePrizeToken;
      break;
    default:
      // 入金+交易项目
      prizePool = plus(
        plus(data?.newUserPrizeTotal, data?.oldUserPrizeTotal),
        data?.tradeUserPrizeTotal
      );
      prizeToken = depositPrizeToken;
  }

  // 处理官网&白皮书
  const getWebside = () => {
    const handleClick = (url) => {
      if (url) {
        if (isApp()) {
          h5Jump(url);
          return;
        }
        pcJump(url);
      }
    };
    return (
      <>
        {tokenDetail?.officialWebsiteUrl ? (
          <div
            className={detailStyles.tag}
            onClick={() => {
              handleClick(tokenDetail.officialWebsiteUrl);
            }}
          >
            {t('detailHeaderWebsite')}
          </div>
        ) : null}
        {tokenDetail?.whitePaperUrl ? (
          <div
            className={detailStyles.tag}
            onClick={() => {
              handleClick(tokenDetail.whitePaperUrl);
            }}
          >
            {t('detailHeaderWhitepaper')}
          </div>
        ) : null}
      </>
    );
  };

  return (
    <div className={detailStyles.header}>
      <div className={detailStyles.wrapper}>
        <div className={detailStyles.breadcrumb}>{toper}</div>
        <div className={detailStyles.container}>
          <div className={detailStyles.containerInfo}>
            <div
              className={detailStyles.logo}
              style={{
                backgroundImage: `url(${getProcessedUrl(
                  data?.icon || '',
                  {}
                )})`,
              }}
            />
            {isPc ? (
              <div className={detailStyles.content}>
                <div
                  className={detailStyles.type}
                  dangerouslySetInnerHTML={{
                    __html: t(typeMap[data?.taskType || TaskType.All], {
                      token: `<b>${token || ''}</b>`,
                    }),
                  }}
                />
                <div
                  className={detailStyles.title}
                  dangerouslySetInnerHTML={{
                    __html: t('detailHeaderTitle', {
                      prize_pool: safeToFormat(prizePool),
                      prize_token: prizeToken || '',
                    }),
                  }}
                />
                <AutoEllipsisText
                  textClassName={detailStyles.subTitle}
                  text={data?.remark}
                  maxLine={2}
                />
                <div className={detailStyles.tags}>
                  {getWebside()}
                  {data?.socialMediaList ? (
                    <Media
                      media={data?.socialMediaList}
                      onAction={(e) => {
                        if (e.mediaUrl) {
                          if (isApp()) {
                            h5Jump(e.mediaUrl);
                            // h5Jump(`bybitapp://open/web?url=${e.mediaUrl}`)
                            return;
                          }
                          pcJump(e.mediaUrl);
                        }
                      }}
                    />
                  ) : null}
                </div>
              </div>
            ) : (
              <div className={detailStyles.contentH5}>
                <div className={detailStyles.contentHeader}>
                  <div className={detailStyles.titleWrapper}>
                    <div
                      className={detailStyles.type}
                      dangerouslySetInnerHTML={{
                        __html: t(typeMap[data?.taskType || TaskType.All], {
                          token: `<b>${token || ''}</b>`,
                        }),
                      }}
                    />
                    <div
                      className={detailStyles.title}
                      dangerouslySetInnerHTML={{
                        __html: t('detailHeaderTitle', {
                          prize_pool: safeToFormat(prizePool),
                          prize_token: prizeToken || '',
                        }),
                      }}
                    />
                  </div>
                  <div
                    className={detailStyles.logoH5}
                    style={{
                      backgroundImage: `url(${getProcessedUrl(
                        data?.icon || '',
                        {}
                      )})`,
                    }}
                  />
                </div>
                <div className={detailStyles.contentFooter}>
                  <div className={detailStyles.subTitleWrapper}>
                    <span
                      className={detailStyles.subTitle}
                      onClick={() => {
                        setRemarkVisible(true);
                      }}
                    >
                      {data?.remark}
                    </span>
                    <div className={detailStyles.shareH5} onClick={handleShare}>
                      <ShareIcon />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className={detailStyles.share} onClick={handleShare}>
            <img
              className={detailStyles.shareIcon}
              src="/bycsi-root/bybit/deadpool/image-7855be96e3a4404b8d21272700d01c2e.svg"
              alt="icon-coin"
            />
            {gt(data?.prizePool, 0) && data?.awardToken ? (
              <div className={detailStyles.shareText}>
                {t('shareToEarn')}
                <b>{safeToFormat(data.prizePool, 4)}</b>
                <b style={{ marginLeft: 0 }}>{data.awardToken}</b>
              </div>
            ) : (
              <span>{t('shareToEarn')}</span>
            )}
            <i className="moly-iconfont bit-icon_share" />
          </div>
        </div>
      </div>
      <Modal
        isMobile={true}
        className={detailStyles.remarkDlg}
        title={token}
        footer={
          <Button
            variant="primary"
            block
            onClick={() => setRemarkVisible(false)}
          >
            {t('ok')}
          </Button>
        }
        onCancel={() => setRemarkVisible(false)}
        open={remarkVisible}
      >
        <div className={detailStyles.dlgText}>{data?.remark}</div>
      </Modal>
    </div>
  );
};
